import isEmpty from "lodash/isEmpty.js"
import { pickRandomItem } from "@/utils/helpers.js"

const BASE_URL = process.env.serverPath
const BASE_URL_V2 = process.env.BASE_URL_V2

function getInterestingSlot(state, categoryId) {
  if (categoryId) {
    const bucket = state.categoryInteresting[categoryId] || {}
    return {
      artSum: bucket.artSum,
      randomObj: bucket.randomObj,
      obj: bucket.obj,
    }
  }

  return {
    artSum: state.mainArtInterestingSum,
    randomObj: state.mainRandomObjInteresting,
    obj: state.mainObjInteresting,
  }
}

function withCategory(categoryId, params) {
  if (!categoryId) {
    return params
  }
  return { ...params, category: categoryId }
}

export const state = () => ({
  totalCountArts: null,
  totalCountObjects: null,
  categoriesCarousel: [],
  mainArtInterestingSum: null,
  mainRandomObjInteresting: null,
  mainObjInteresting: null,
  categoryInteresting: {},
})

export const mutations = {
  SAVE_TOTAL_COUNT_ARTS(state, payload) {
    state.totalCountArts = payload
  },
  SAVE_TOTAL_COUNT_OBJECTS(state, payload) {
    state.totalCountObjects = payload
  },
  SAVE_CATEGORIES_CAROUSEL(state, payload) {
    state.categoriesCarousel = payload
  },
  SAVE_MAIN_ART_INTERESTING_SUM(state, payload) {
    state.mainArtInterestingSum = payload
  },
  SAVE_MAIN_RANDOM_OBJ_INTERESTING(state, payload) {
    state.mainRandomObjInteresting = payload
  },
  SAVE_MAIN_OBJ_INTERESTING(state, payload) {
    state.mainObjInteresting = payload
  },
  SET_CATEGORY_INTERESTING_ITEM(state, { categoryId, field, value }) {
    if (!state.categoryInteresting[categoryId]) {
      state.categoryInteresting = {
        ...state.categoryInteresting,
        [categoryId]: { artSum: null, randomObj: null, obj: null },
      }
    }
    state.categoryInteresting[categoryId][field] = value
  },
}

export const actions = {
  async fetchDataHeader({ dispatch, query, state }) {
    await dispatch('getTotalCountArts')
    await dispatch('getTotalCountObjects')
  },

  async getTotalCountArts({ state, commit }) {
    if (isEmpty(state.totalCountArts)) {
      const response = await this.$axios.$get(`${BASE_URL_V2}/public/materials/?only_count=1`)

      if(response?.total){
        commit('SAVE_TOTAL_COUNT_ARTS', response.total )
        return response.total
      }

      return null
    }

    return state.totalCountArts
  },

  async getTotalCountObjects({ state, commit }) {
    if (isEmpty(state.totalCountObjects)) {
      const response = await this.$axios.$get(`${BASE_URL}/public/objects/?is_about_project=false&with_events=true&only_count=1`)

      if(response?.total){
        commit('SAVE_TOTAL_COUNT_OBJECTS', response.total )
        return response.total
      }

      return null
    }

    return state.totalCountObjects
  },

  async fetchDataCarousel({ state, commit }) {
    if (isEmpty(state.categoriesCarousel)) {
      const response = await this.$axios.$get(`${BASE_URL_V2}/public/object_categories/tree/`)
      commit('SAVE_CATEGORIES_CAROUSEL', response )
      return response.total
    }

    return state.totalCountObjects
  },

  async fetchDataInteresting({ dispatch }, params = {}) {
    await dispatch('getMainArtInterestingSum', params)
    await dispatch('getMainObjInteresting', params)
    await dispatch('getMainRandomObjInteresting', params)
  },

  saveInterestingArtSum({ commit }, { categoryId, item }) {
    if (categoryId) {
      commit('SET_CATEGORY_INTERESTING_ITEM', { categoryId, field: 'artSum', value: item })
    } else {
      commit('SAVE_MAIN_ART_INTERESTING_SUM', item)
    }
  },

  saveInterestingRandomObj({ commit }, { categoryId, item }) {
    if (categoryId) {
      commit('SET_CATEGORY_INTERESTING_ITEM', { categoryId, field: 'randomObj', value: item })
    } else {
      commit('SAVE_MAIN_RANDOM_OBJ_INTERESTING', item)
    }
  },

  saveInterestingObj({ commit }, { categoryId, item }) {
    if (categoryId) {
      commit('SET_CATEGORY_INTERESTING_ITEM', { categoryId, field: 'obj', value: item })
    } else {
      commit('SAVE_MAIN_OBJ_INTERESTING', item)
    }
  },

  async getMainArtInterestingSum({ dispatch, state, commit }, params = {}) {
    const categoryId = params.category || null
    const slot = getInterestingSlot(state, categoryId)

    if (!isEmpty(slot.artSum)) {
      return slot.artSum
    }

    const articlesResponse = await dispatch('art/publicObjects', withCategory(categoryId, {
      limit: 5,
      offset: 0,
      order: '-total_score',
    }), { root: true })

    let item = null
    let itemType = 'article'

    if (articlesResponse?.items?.length) {
      item = pickRandomItem(articlesResponse.items)
    } else {
      const exclude = [slot.obj && slot.obj.id, slot.randomObj && slot.randomObj.id].filter(Boolean).join(',')
      const objectsResponse = await dispatch('objects/publicObjects', withCategory(categoryId, {
        limit: 5,
        offset: 0,
        order: '-total_score',
        exclude,
        is_about_project: false,
      }), { root: true })

      if (objectsResponse?.items?.length) {
        item = pickRandomItem(objectsResponse.items)
        itemType = 'object'
      }
    }

    if (!item) {
      return null
    }

    if (itemType === 'article') {
      try {
        const summaryObjects = await this.$axios.$get(`${BASE_URL_V2}/public/materials/${item.id}/summary/`)
        if (summaryObjects?.is_summary && summaryObjects?.attrs?.items?.length) {
          item.attrs_list = summaryObjects.attrs.items.slice(0, 6)
        }
      } catch (err) {
        console.error('not have summary');
      }
    }

    item._interestingItemType = itemType
    await dispatch('saveInterestingArtSum', { categoryId, item })
    return item
  },

  async getMainRandomObjInteresting({ dispatch, state, commit }, params = {}) {
    const categoryId = params.category || null
    const slot = getInterestingSlot(state, categoryId)

    if (!isEmpty(slot.randomObj)) {
      return slot.randomObj
    }

    const exclude = [slot.obj && slot.obj.id, slot.artSum && slot.artSum.id].filter(Boolean).join(',')
    const objects = await dispatch('objects/publicObjects', withCategory(categoryId, {
      limit: 50,
      offset: 0,
      order: '-scoring_score',
      exclude,
      is_about_project: false,
    }), { root: true })

    if (objects?.items?.length) {
      const item = pickRandomItem(objects.items)
      await dispatch('saveInterestingRandomObj', { categoryId, item })
    }

    return getInterestingSlot(state, categoryId).randomObj
  },

  async getMainObjInteresting({ dispatch, state, commit }, params = {}) {
    const categoryId = params.category || null
    const slot = getInterestingSlot(state, categoryId)

    if (!isEmpty(slot.obj)) {
      return slot.obj
    }

    const exclude = [slot.randomObj && slot.randomObj.id, slot.artSum && slot.artSum.id].filter(Boolean).join(',')
    const objects = await dispatch('objects/publicObjects', withCategory(categoryId, {
      limit: 10,
      offset: 0,
      order: '-total_score',
      exclude,
      with_events: true,
      is_about_project: false,
    }), { root: true })

    if (objects?.items?.length) {
      const item = pickRandomItem(objects.items)
      await dispatch('saveInterestingObj', { categoryId, item })
    }

    return getInterestingSlot(state, categoryId).obj
  },
}

export const getters = {
  getTotalCountArts: (state) => {
    return state.totalCountArts
  },
  getTotalCountObjects: (state) => {
    return state.totalCountObjects
  },
  getDataCarousel: (state) => {
    return state.categoriesCarousel
  },
  getMainArtInterestingSum: (state) => (categoryId) => {
    return getInterestingSlot(state, categoryId || null).artSum
  },
  getMainObjInteresting: (state) => (categoryId) => {
    return getInterestingSlot(state, categoryId || null).obj
  },
  getMainRandomObjInteresting: (state) => (categoryId) => {
    return getInterestingSlot(state, categoryId || null).randomObj
  },
}
